// Icons by what they mean, never by which drawing (LOOKS.md Part 1, "Icons").
// A screen asks for `trade`; the look decides it is Tabler's arrows in Clean
// and a hand-drawn swap in Crayon. Clean's set is here and complete; a look
// redraws whichever meanings it wants in its own folder (looks/<id>/index.ts,
// `icons`), and a clean screen (eslint.config.js, CLEAN_SCREENS) may not
// import an icon library at all.
//
// The stroke is the look's too: `.fsp-icon` sets stroke-width from
// --fsp-icon-stroke, which beats the attribute Tabler writes.
//
// Add a name when a screen needs a meaning the list lacks. Name the meaning
// ("withdraw"), not the picture ("x").

import {
  IconAlarm,
  IconAlbum,
  IconAlertTriangle,
  IconAnchor,
  IconAntenna,
  IconArmchair,
  IconArrowBackUp,
  IconArrowBarToUp,
  IconArrowDown,
  IconArrowLeft,
  IconArrowRight,
  IconArrowUp,
  IconArrowsDiagonal,
  IconArrowsDiagonalMinimize2,
  IconArrowsExchange,
  IconArrowsShuffle,
  IconArrowsUpDown,
  IconArticle,
  IconAward,
  IconBallAmericanFootball,
  IconBandage,
  IconBeach,
  IconBell,
  IconBinoculars,
  IconBolt,
  IconBook,
  IconBooks,
  IconBriefcase,
  IconBrush,
  IconCake,
  IconCalendar,
  IconCalendarEvent,
  IconCalendarX,
  IconCamera,
  IconChartBar,
  IconChartBarPopular,
  IconCheck,
  IconCheckbox,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconCircles,
  IconColorPicker,
  IconClipboard,
  IconClock,
  IconClockPlay,
  IconCompass,
  IconConfetti,
  IconCopy,
  IconCrown,
  IconCrystalBall,
  IconDeviceDesktopDown,
  IconDeviceTv,
  IconDeviceMobile,
  IconDevices,
  IconDiamond,
  IconDice5,
  IconDog,
  IconDots,
  IconDotsVertical,
  IconDownload,
  IconUpload,
  IconEye,
  IconEyeglass,
  IconFeather,
  IconFlag,
  IconFlame,
  IconGavel,
  IconGhost,
  IconGift,
  IconHammer,
  IconHeart,
  IconHeartBroken,
  IconHeartHandshake,
  IconHistory,
  IconHome,
  IconHourglass,
  IconInfinity,
  IconInfoCircle,
  IconLamp,
  IconLayoutColumns,
  IconLayoutGrid,
  IconLetterW,
  IconLetterY,
  IconList,
  IconListCheck,
  IconListNumbers,
  IconLock,
  IconLockOpen,
  IconLogout,
  IconMagnet,
  IconMail,
  IconMasksTheater,
  IconMaximize,
  IconMeat,
  IconMedal,
  IconMessage,
  IconMessageCircle,
  IconMinimize,
  IconMinus,
  IconMoodHappy,
  IconMoodPlus,
  IconMoodSmile,
  IconMoon,
  IconMoonStars,
  IconMountain,
  IconMovie,
  IconNews,
  IconPaint,
  IconPalette,
  IconPencil,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlayerSkipForward,
  IconPlus,
  IconPodium,
  IconRefresh,
  IconRepeat,
  IconReplace,
  IconRoad,
  IconRobot,
  IconRocket,
  IconRotate,
  IconRoute,
  IconScale,
  IconSchool,
  IconScoreboard,
  IconScript,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShare2,
  IconShieldCheck,
  IconShoe,
  IconSkull,
  IconSofa,
  IconSparkles,
  IconSpeakerphone,
  IconSquareCheck,
  IconSquarePlus,
  IconStar,
  IconStarFilled,
  IconSticker,
  IconStopwatch,
  IconSun,
  IconSunMoon,
  IconSunrise,
  IconSwitchHorizontal,
  IconSword,
  IconSwords,
  IconTag,
  IconTarget,
  IconTelescope,
  IconTemperature,
  IconTicket,
  IconToiletPaper,
  IconTournament,
  IconTrash,
  IconTree,
  IconTrophy,
  IconUser,
  IconUserPlus,
  IconUsers,
  IconUsersGroup,
  IconVs,
  IconWalk,
  IconX,
  IconZoomIn,
  IconZzz,
} from '@tabler/icons-react';
import { useLook } from '../data/look';
import type { Drawing } from '../looks/define';
import { lookOf } from '../looks/registry';

const CLEAN = {
  add: IconPlus,
  remove: IconMinus,
  accept: IconCheck,
  decline: IconX,
  withdraw: IconX,
  close: IconX,
  edit: IconPencil,
  delete: IconTrash,
  trade: IconArrowsExchange,
  /** A player moving within his own lineup: the badge's cue (B-288). */
  move: IconArrowsUpDown,
  chat: IconMessage,
  send: IconSend,
  /** The chat box's emoji picker (PLAN-2026-10-27-more-fun-in-chat C1). */
  emoji: IconMoodHappy,
  /** Any emoji as a reaction, past the quick five. */
  react: IconMoodPlus,
  /** The chat box's stickers (PLAN-2026-10-30-stickers). */
  sticker: IconSticker,
  /** A poll in the league's chat (PLAN-2026-10-30-polls-in-chat). */
  poll: IconChartBarPopular,
  // Worth a look's first of a position, and its door (PLAN-2026-10-27-worth-a-look).
  flame: IconFlame,
  notifications: IconBell,
  veto: IconGavel,
  locked: IconLock,
  unlock: IconLockOpen,
  preview: IconEye,
  pending: IconClock,
  when: IconCalendarEvent,
  /** The Calendar tab (PLAN-2026-10-30-the-calendar). */
  calendar: IconCalendar,
  trophy: IconTrophy,
  favorite: IconStar,
  favorited: IconStarFilled,
  person: IconUser,
  people: IconUsers,
  invite: IconUserPlus,
  search: IconSearch,
  share: IconShare,
  copy: IconCopy,
  download: IconDownload,
  // Bringing a file in (DIRECTION A13): a league or a team in the open format.
  upload: IconUpload,
  // Installing the app (INSTALL.md §4.3): putting it on this device, and the
  // three things a browser's own steps say to tap — an Apple device's Share
  // button, Add to Home Screen, and a browser's menu.
  install: IconDeviceDesktopDown,
  'share-sheet': IconShare2,
  'add-to-home': IconSquarePlus,
  'browser-menu': IconDotsVertical,
  info: IconInfoCircle,
  // Where the data comes from, with thanks (B-592).
  credits: IconHeartHandshake,
  warning: IconAlertTriangle,
  up: IconArrowUp,
  top: IconArrowBarToUp,
  down: IconArrowDown,
  back: IconArrowLeft,
  forward: IconArrowRight,
  next: IconChevronRight,
  previous: IconChevronLeft,
  expand: IconChevronDown,
  // A panel taking the room and giving it back (PLAN-2026-10-17 §2.3): never
  // an arrow, which means the next in a sequence (UX-41).
  grow: IconArrowsDiagonal,
  shrink: IconArrowsDiagonalMinimize2,
  settings: IconSettings,
  more: IconDots,
  // The draft on a TV (M3), and the whole screen for it.
  tv: IconDeviceTv,
  'full-screen': IconMaximize,
  'shrink-screen': IconMinimize,
  undo: IconArrowBackUp,
  redo: IconRotate,
  refresh: IconRefresh,
  play: IconPlayerPlay,
  pause: IconPlayerPause,
  // To the end of something that plays: a replay (C11).
  'to-end': IconPlayerSkipForward,
  'play-at': IconClockPlay,
  live: IconBolt,
  signout: IconLogout,
  ticket: IconTicket,
  bot: IconRobot,
  random: IconDice5,
  crown: IconCrown,
  sport: IconBallAmericanFootball,
  // The places a league has, by what each is.
  home: IconHome,
  draft: IconVs,
  matchup: IconSwords,
  scoreboard: IconChartBar,
  // Players side by side (Compare, its tray).
  compare: IconLayoutColumns,
  recap: IconNews,
  // The league's season once its champion is crowned (H9).
  yearbook: IconAlbum,
  news: IconArticle,
  waivers: IconReplace,
  standings: IconListNumbers,
  history: IconHistory,
  rules: IconBook,
  looks: IconPalette,
  // The face maker (B-488): a face, the paint can and the dropper.
  face: IconMoodSmile,
  paint: IconPaint,
  dropper: IconColorPicker,
  // Mode, by what it is set to (the account menu's Mode).
  dark: IconMoon,
  light: IconSun,
  system: IconSunMoon,
  leagues: IconLayoutGrid,
  list: IconList,
  podium: IconPodium,
  achievements: IconAward,
} satisfies Record<string, Drawing>;

export type IconName = keyof typeof CLEAN;

const SIZE = { xs: 12, sm: 14, md: 18, lg: 22, xl: 26 } as const;

/**
 * Decorative by default: an icon beside a word adds nothing for a screen
 * reader. An icon that stands alone belongs inside a control that carries the
 * name (`aria-label`), which is the kit button's job, not the icon's.
 */
export function Icon({ name, size = 'md' }: { name: IconName; size?: keyof typeof SIZE }) {
  // The look's own drawing when it has one; Clean's otherwise, which the
  // look's stylesheet still draws in its own stroke and filter.
  const Drawn = lookOf(useLook()).icons?.[name] ?? CLEAN[name];
  return <Drawn size={SIZE[size]} className="fsp-icon" aria-hidden />;
}

// ── Award art ──────────────────────────────────────────────────────────────
//
// An award names its glyph by id (`@fsp/awards`: "crown", "toilet", "flame"),
// and the glyph is ART, the axis LOOKS.md Part 1 says a look owns most of all:
// Crayon draws its own. So the map from an award's id to a drawing lives here,
// one per look, and a screen asks for `<AwardGlyph icon={card.icon} />`.

const CLEAN_AWARDS: Record<string, Drawing> = {
  crown: IconCrown,
  toilet: IconToiletPaper,
  target: IconTarget,
  bolt: IconBolt,
  armchair: IconArmchair,
  magnet: IconMagnet,
  'heart-broken': IconHeartBroken,
  dice: IconDice5,
  star: IconStar,
  rocket: IconRocket,
  trophy: IconTrophy,
  medal: IconMedal,
  chart: IconChartBar,
  flame: IconFlame,
  sword: IconSword,
  ghost: IconGhost,
  shield: IconShieldCheck,
  mountain: IconMountain,
  sparkles: IconSparkles,
  hammer: IconHammer,
  skull: IconSkull,
  podium: IconPodium,
  telescope: IconTelescope,
  anchor: IconAnchor,
  briefcase: IconBriefcase,
  flag: IconFlag,
  lock: IconLock,
};

/** An award's glyph by its id; an id no set draws is a medal. */
export function AwardGlyph({ icon, size = 18 }: { icon: string; size?: number }) {
  const Drawn = lookOf(useLook()).awards?.[icon] ?? CLEAN_AWARDS[icon] ?? IconMedal;
  return <Drawn size={size} className="fsp-icon" aria-hidden />;
}

// ── Achievement art (ACHIEVEMENTS.md §11) ──────────────────────────────────
//
// An account achievement names its glyph by id ("goalpost", "owl", "cake"),
// the way an award does, and a client draws a default for one it does not
// know (an icon on the wire is a plain string). A look's award drawings
// are asked first, so a look that draws a "trophy" or a "medal" draws it here
// too.

const CLEAN_ACHIEVEMENTS: Record<string, Drawing> = {
  antenna: IconAntenna,
  applause: IconConfetti,
  ballot: IconCheckbox,
  'ballot-box': IconCheckbox,
  bell: IconBell,
  binoculars: IconBinoculars,
  bird: IconFeather,
  book: IconBook,
  boot: IconShoe,
  bracket: IconTournament,
  briefcase: IconBriefcase,
  bullseye: IconTarget,
  buzzer: IconAlarm,
  cake: IconCake,
  calendar: IconCalendar,
  'calendar-x': IconCalendarX,
  camera: IconCamera,
  cap: IconSchool,
  chat: IconMessage,
  checkbox: IconSquareCheck,
  checklist: IconListCheck,
  clipboard: IconClipboard,
  compass: IconCompass,
  cracked: IconBandage,
  crowd: IconUsersGroup,
  crown: IconCrown,
  'crystal-ball': IconCrystalBall,
  devices: IconDevices,
  dog: IconDog,
  envelope: IconMail,
  fifty: IconAward,
  film: IconMovie,
  flag: IconFlag,
  flame: IconFlame,
  flip: IconSwitchHorizontal,
  football: IconBallAmericanFootball,
  footprints: IconWalk,
  gavel: IconGavel,
  gift: IconGift,
  goalpost: IconLetterY,
  hammock: IconZzz,
  handshake: IconHeartHandshake,
  heart: IconHeart,
  hourglass: IconHourglass,
  house: IconHome,
  island: IconBeach,
  juggler: IconCircles,
  lightning: IconBolt,
  loop: IconInfinity,
  magnifier: IconZoomIn,
  medal: IconMedal,
  megaphone: IconSpeakerphone,
  monocle: IconEyeglass,
  moon: IconMoon,
  owl: IconMoonStars,
  paintbrush: IconBrush,
  palette: IconPalette,
  pencil: IconPencil,
  phone: IconDeviceMobile,
  repeat: IconRepeat,
  ring: IconDiamond,
  road: IconRoad,
  rocket: IconRocket,
  scales: IconScale,
  scoreboard: IconScoreboard,
  scroll: IconScript,
  shelf: IconBooks,
  shield: IconShieldCheck,
  shuffle: IconArrowsShuffle,
  snake: IconRoute,
  sneaker: IconShoe,
  sofa: IconSofa,
  sparkle: IconSparkles,
  speech: IconMessageCircle,
  spotlight: IconLamp,
  star: IconStar,
  sticker: IconSticker,
  stopwatch: IconStopwatch,
  sunrise: IconSunrise,
  tag: IconTag,
  theater: IconMasksTheater,
  thermometer: IconTemperature,
  ticket: IconTicket,
  tree: IconTree,
  trophy: IconTrophy,
  turkey: IconMeat,
  w: IconLetterW,
  whistle: IconSpeakerphone,
};

/** Whether Clean draws a glyph by this id, an award's or an achievement's (B-440's test). */
export function cleanDraws(icon: string): boolean {
  return CLEAN_AWARDS[icon] !== undefined || CLEAN_ACHIEVEMENTS[icon] !== undefined;
}

/** An achievement's or a family's glyph by its id; one nobody draws is a sparkle. */
export function AchievementGlyph({ icon, size = 18 }: { icon: string; size?: number }) {
  const Drawn = lookOf(useLook()).awards?.[icon] ?? CLEAN_ACHIEVEMENTS[icon] ?? IconSparkles;
  return <Drawn size={size} className="fsp-icon" aria-hidden />;
}

/**
 * A team's icon badge (B-440): the look's drawing, else Clean's award or
 * achievement drawing by that id — the same two maps `cleanDraws` checks, so
 * every badge a team can pick draws as itself and none falls to the sparkle.
 */
export function BadgeGlyph({ icon, size = 18 }: { icon: string; size?: number }) {
  const Drawn =
    lookOf(useLook()).awards?.[icon] ??
    CLEAN_AWARDS[icon] ??
    CLEAN_ACHIEVEMENTS[icon] ??
    IconSparkles;
  return <Drawn size={size} className="fsp-icon" aria-hidden />;
}
